import { ArrowLeft, Check, CreditCard, Loader, RefreshCw, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const PULL_REFRESH_DISTANCE = 72;

export function shouldTriggerPullRefresh(startY, endY, scrollY) {
  return scrollY === 0 && endY - startY >= PULL_REFRESH_DISTANCE;
}

function formatCurrency(value, currency, digits = 2) {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(amount);
  } catch {
    return `${amount.toFixed(digits)} ${currency}`;
  }
}

function formatINR(value) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function estimatedCurrency(method, response) {
  return (method?.estimated_currency || response?.receiving_currency || 'INR').trim().toUpperCase();
}

function estimatedValue(method) {
  return method?.estimated_amount ?? method?.estimated_inr;
}

function formatEstimatedAmount(method, response) {
  const currency = estimatedCurrency(method, response);
  const value = estimatedValue(method);
  return currency === 'INR' ? formatINR(value) : formatCurrency(value, currency);
}

function effectiveReceivingRate(method) {
  return method?.effective_fiat_to_receiving_rate ?? method?.effective_fiat_to_inr_rate;
}

function selectedMethod(record, selectedPaymentMethods) {
  const methodID = selectedPaymentMethods[record.onramp_id] || record.selected_payment_method;
  return record.payment_methods.find((method) => method.payment_method === methodID) || record.payment_methods[0];
}

const OnrampQuoteList = ({
  response,
  selectedOnrampId,
  selectedPaymentMethods = {},
  onSelectOnramp,
  onSelectPaymentMethod,
  onRefresh,
  onContinue,
  onBack,
  refreshing = false,
  refreshProgress = null,
  creating = false,
  stale = false,
}) => {
  const pullStart = useRef(null);
  const pullEnd = useRef(null);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const openerRef = useRef(null);
  const [detailsRecordId, setDetailsRecordId] = useState(null);
  const [detailsMethodId, setDetailsMethodId] = useState(null);
  const records = response?.onramps || [];
  const activeRecord = records.find((record) => record.onramp_id === selectedOnrampId) || records[0];
  const activeMethod = activeRecord ? selectedMethod(activeRecord, selectedPaymentMethods) : null;
  const detailsRecord = records.find((record) => record.onramp_id === detailsRecordId) || null;
  const detailsMethod = detailsRecord
    ? detailsRecord.payment_methods.find((method) => method.payment_method === detailsMethodId) || selectedMethod(detailsRecord, selectedPaymentMethods)
    : null;
  const countdownProgress = refreshProgress == null
    ? 0
    : Math.min(1, Math.max(0, refreshProgress));
  const refreshLabel = 'Refresh quotes';

  useEffect(() => {
    if (!detailsRecord) return undefined;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setDetailsRecordId(null);
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      if (openerRef.current && document.contains(openerRef.current)) openerRef.current.focus();
    };
  }, [detailsRecord]);

  const openDetails = (record, opener = null) => {
    const method = selectedMethod(record, selectedPaymentMethods);
    openerRef.current = opener;
    setDetailsRecordId(record.onramp_id);
    setDetailsMethodId(method?.payment_method || null);
  };

  const closeDetails = () => setDetailsRecordId(null);

  return (
    <>
      <div
        className="space-y-5"
        onTouchStart={(event) => {
          if (window.scrollY === 0) {
            pullStart.current = event.touches[0]?.clientY ?? null;
            pullEnd.current = pullStart.current;
          }
        }}
        onTouchMove={(event) => {
          if (pullStart.current != null) pullEnd.current = event.touches[0]?.clientY ?? pullEnd.current;
        }}
        onTouchEnd={() => {
          if (pullStart.current != null && pullEnd.current != null &&
            shouldTriggerPullRefresh(pullStart.current, pullEnd.current, window.scrollY)) {
            onRefresh();
          }
          pullStart.current = null;
          pullEnd.current = null;
        }}
      >
      <div className="flex items-start justify-between gap-4">
        <div>
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="mb-2 inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600 focus-visible:ring-offset-2"
              aria-label="Back to review"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              <span>Back</span>
            </button>
          ) : null}
          <h2 className="text-xl font-bold text-slate-900">Choose how to pay</h2>
          <p className="mt-1 text-sm text-slate-500">Compare the live amount your recipient is estimated to receive.</p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing || creating}
          className="flex h-10 w-10 shrink-0 items-center justify-center border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          aria-label={refreshLabel}
          title={refreshLabel}
        >
          <span
            data-refresh-progress
            className="relative flex h-7 w-7 items-center justify-center rounded-full"
            style={{
              background: refreshProgress > 0
                ? `conic-gradient(rgb(14 116 144) ${countdownProgress * 360}deg, rgb(226 232 240) 0deg)`
                : undefined,
            }}
          >
            <span className="absolute inset-[3px] rounded-full bg-white" />
            {refreshing ? (
              <RefreshCw className="relative h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw className="relative h-4 w-4" aria-hidden="true" />
            )}
          </span>
        </button>
      </div>

      <div className="flex items-center justify-between border-y border-slate-200 py-3 text-xs text-slate-500">
        <span>Updated {response?.quoted_at ? new Date(response.quoted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'just now'}</span>
        <span className={stale ? 'font-semibold text-amber-700' : 'text-slate-500'}>{stale ? 'Refresh required' : 'Live estimates'}</span>
      </div>

      <div className="space-y-3" role="radiogroup" aria-label="Onramp providers">
        {records.map((record) => {
          const method = selectedMethod(record, selectedPaymentMethods);
          const selected = record.onramp_id === (selectedOnrampId || records[0]?.onramp_id);
          return (
            <div
              key={record.onramp_id}
              data-quote-record={record.onramp_id}
              role="button"
              tabIndex={0}
              aria-label={`View ${record.onramp_name} pricing details`}
              onClick={(event) => {
                if (event.target.closest('input, select, button, a, textarea, label')) return;
                openDetails(record, event.currentTarget);
              }}
              onKeyDown={(event) => {
                if ((event.key === 'Enter' || event.key === ' ') && !event.target.closest('input, select, button, a, textarea, label')) {
                  event.preventDefault();
                  openDetails(record, event.currentTarget);
                }
              }}
              className={`cursor-pointer border p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600 focus-visible:ring-offset-2 ${selected ? 'border-cyan-700 bg-cyan-50/40' : 'border-slate-200 bg-white hover:border-slate-300'}`}
            >
              <div className="flex items-start gap-3">
                <label className="mt-1 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center" aria-label={`Select ${record.onramp_name}`}>
                  <input
                    type="radio"
                    name="onramp-provider"
                    value={record.onramp_id}
                    checked={selected}
                    onChange={() => onSelectOnramp(record.onramp_id)}
                    className="h-4 w-4 accent-cyan-700"
                  />
                </label>
                <span className="block min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-3">
                    <span className="truncate font-semibold text-slate-900">{record.onramp_name}</span>
                    {record.best_rate ? <span className="border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">Best rate</span> : null}
                  </span>
                  <span className="mt-3 grid grid-cols-2 gap-4">
                    <span>
                      <span className="block text-[11px] uppercase text-slate-500">You pay</span>
                      <span className="mt-0.5 block text-base font-semibold text-slate-900">
                        {formatCurrency(method.source_fiat_amount, method.source_fiat_currency)}
                      </span>
                    </span>
                    <span className="text-right">
                      <span className="block text-[11px] uppercase text-slate-500">Estimated {estimatedCurrency(method, response)}</span>
                      <span className="mt-0.5 block text-lg font-bold text-emerald-700">{formatEstimatedAmount(method, response)}</span>
                    </span>
                  </span>
                </span>
              </div>

              <div className="mt-4 border-t border-slate-200 pt-3">
                <label className="block text-xs font-medium text-slate-600" htmlFor={`payment-${record.onramp_id}`}>Payment method</label>
                <div className="relative mt-1.5">
                  <CreditCard className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <select
                    id={`payment-${record.onramp_id}`}
                    value={method.payment_method}
                    onChange={(event) => onSelectPaymentMethod(record.onramp_id, event.target.value)}
                    className="w-full border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-800 outline-none focus:border-cyan-700 focus:ring-1 focus:ring-cyan-700"
                  >
                    {record.payment_methods.map((option) => (
                      <option key={option.payment_method} value={option.payment_method}>{option.payment_method_name}</option>
                    ))}
                  </select>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="block text-slate-500">Stablecoin</span>
                    <span className="font-semibold text-slate-800">{Number(method.expected_crypto_amount).toFixed(2)} {method.crypto_currency}</span>
                  </div>
                  <div className="text-right">
                    <span className="block text-slate-500">Fee</span>
                    <span className="font-semibold text-slate-800">{formatCurrency(method.total_fee, method.source_fiat_currency)}</span>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3 text-xs">
                  <span className="text-slate-500">Effective rate</span>
                  <span className="text-right font-semibold text-slate-800">
                    1 {method.source_fiat_currency} ≈ {Number(effectiveReceivingRate(method)).toFixed(2)} {estimatedCurrency(method, response)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="btn-primary flex w-full items-center justify-center gap-2"
        disabled={!activeMethod || creating || stale}
        onClick={() => activeMethod && onContinue(activeMethod.selection_token)}
      >
        {creating ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        <span>{creating ? 'Opening checkout' : 'Continue'}</span>
      </button>
      <p className="text-center text-[11px] leading-4 text-slate-500">{estimatedCurrency(activeMethod, response)} amounts are estimates and may change before payment completes.</p>
      </div>

      {detailsRecord && detailsMethod ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/45 p-0 sm:items-center sm:p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeDetails();
          }}
        >
          <section
            ref={dialogRef}
            className="max-h-[90dvh] w-full overscroll-contain overflow-y-auto border border-slate-200 bg-white p-5 shadow-2xl sm:max-w-lg sm:rounded-md sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quote-details-title"
            onKeyDown={(event) => {
              if (event.key !== 'Tab' || !dialogRef.current) return;
              const focusable = [...dialogRef.current.querySelectorAll('button, select, [href], input, textarea, [tabindex]:not([tabindex="-1"])')].filter((element) => !element.disabled);
              if (!focusable.length) return;
              const first = focusable[0];
              const last = focusable[focusable.length - 1];
              if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
              } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
              }
            }}
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-cyan-700">Quote details</p>
                <h2 id="quote-details-title" className="mt-1 text-lg font-bold text-slate-900">{detailsRecord.onramp_name}</h2>
              </div>
              <button
                type="button"
                onClick={closeDetails}
                ref={closeButtonRef}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600"
                aria-label="Close quote details"
                title="Close"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            {detailsRecord.payment_methods.length > 1 ? (
              <label className="mt-4 block text-xs font-medium text-slate-600" htmlFor="quote-details-payment-method">
                Payment method
                <select
                  id="quote-details-payment-method"
                  value={detailsMethod.payment_method}
                  onChange={(event) => {
                    setDetailsMethodId(event.target.value);
                    onSelectPaymentMethod(detailsRecord.onramp_id, event.target.value);
                  }}
                  className="mt-1.5 w-full border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-cyan-700 focus:ring-1 focus:ring-cyan-700"
                >
                  {detailsRecord.payment_methods.map((method) => (
                    <option key={method.payment_method} value={method.payment_method}>{method.payment_method_name}</option>
                  ))}
                </select>
              </label>
            ) : <p className="mt-4 text-sm font-medium text-slate-700">{detailsMethod.payment_method_name}</p>}

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="border border-slate-200 bg-slate-50 p-3">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">You pay</p>
                <p className="mt-1 text-lg font-bold tabular-nums text-slate-900">{formatCurrency(detailsMethod.source_fiat_amount, detailsMethod.source_fiat_currency)}</p>
              </div>
              <div className="border border-emerald-200 bg-emerald-50 p-3 text-right">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700">Estimated {estimatedCurrency(detailsMethod, response)}</p>
                <p className="mt-1 text-lg font-bold tabular-nums text-emerald-800">{formatEstimatedAmount(detailsMethod, response)}</p>
              </div>
            </div>

            <dl className="mt-5 divide-y divide-slate-100 border-y border-slate-200">
              <div className="flex items-center justify-between gap-4 py-3 text-sm">
                <dt className="text-slate-500">Effective rate</dt>
                <dd className="text-right font-semibold tabular-nums text-slate-900">1 {detailsMethod.source_fiat_currency} ≈ {Number(effectiveReceivingRate(detailsMethod)).toFixed(2)} {estimatedCurrency(detailsMethod, response)}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 py-3 text-sm">
                <dt className="text-slate-500">Stablecoin received</dt>
                <dd className="text-right font-semibold tabular-nums text-slate-900">{Number(detailsMethod.expected_crypto_amount).toFixed(2)} {detailsMethod.crypto_currency}</dd>
              </div>
            </dl>

            <div className="mt-5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fee breakdown</h3>
              {detailsMethod.fee_breakdown?.length ? (
                <div className="mt-2 divide-y divide-slate-100 border-y border-slate-200">
                  {detailsMethod.fee_breakdown.map((fee) => (
                    <div key={`${fee.code}-${fee.label}`} className="py-3">
                      <div className="flex items-start justify-between gap-4 text-sm">
                        <div>
                          <p className="font-medium text-slate-800">{fee.label}</p>
                          {fee.rate != null && fee.rate !== '' ? <p className="mt-0.5 text-xs text-slate-500">{Number(fee.rate).toFixed(2)}%{fee.minimum_amount ? ` · minimum ${formatCurrency(fee.minimum_amount, fee.minimum_currency || fee.currency)}${fee.applied_by_minimum ? ' · applied' : ''}` : ''}</p> : null}
                        </div>
                        <p className="shrink-0 text-right font-semibold tabular-nums text-slate-900">{formatCurrency(fee.amount, fee.currency || detailsMethod.source_fiat_currency)}</p>
                      </div>
                      <p className="mt-1 text-[11px] text-slate-500">{fee.confidence === 'derived' ? 'Calculated from quote' : fee.confidence === 'policy_only' ? 'Provider policy reference' : fee.confidence === 'unavailable' ? 'Not provided by provider' : 'From provider quote'}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-2 border-y border-dashed border-slate-300 py-3 text-sm text-slate-500">Provider did not return a fee breakdown.</p>
              )}
              <div className="mt-3 flex items-center justify-between gap-4 text-sm font-bold text-slate-900">
                <span>Total fee</span>
                <span>{formatCurrency(detailsMethod.fee_total?.amount ?? detailsMethod.total_fee, detailsMethod.fee_total?.currency || detailsMethod.source_fiat_currency)}</span>
              </div>
              {detailsMethod.fee_total?.confidence === 'partial' ? <p className="mt-2 text-xs text-amber-700">Partial fee breakdown: some provider charges may not be listed.</p> : null}
            </div>

            {detailsMethod.pricing_notes?.length ? (
              <div className="mt-5 space-y-1 border-t border-slate-200 pt-4 text-xs text-slate-500">
                {detailsMethod.pricing_notes.map((note) => <p key={note.code}>• {note.label}</p>)}
              </div>
            ) : null}
          </section>
        </div>
      ) : null}
    </>
  );
};

export default OnrampQuoteList;
